import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { UserProfileTheme } from '@prisma/client';

import { useMatchMedia } from '@blockworks/ui/hooks';
import { createSafeContext } from '@blockworks/ui/utils';

import { ColorModeClass } from './theme.model';
import { getThemeCookie, setThemeClassAndCookie } from './theme.utils';

type ThemeName = Exclude<UserProfileTheme, 'System'>;

type ThemeContextValue = {
    /** The current theme (aka color mode) */
    theme: UserProfileTheme;
    /** Sets the current theme (aka color mode) */
    setTheme: (theme: UserProfileTheme) => void;
    /** The explicit name of the color mode, even when theme `System` is selected */
    themeName?: ThemeName;
};

const [ThemeContextProvider, useTheme] = createSafeContext<ThemeContextValue>({
    name: 'ThemeContext',
    strict: false,
    hookName: 'useTheme',
    providerName: 'ThemeProvider',
    defaultValue: {
        theme: UserProfileTheme.Light,
        setTheme: () => null,
        themeName: 'Light',
    },
});

const useUpdateThemeOnMediaQueryUpdate = () => {
    const { theme, setTheme } = useTheme();
    const prefersDarkMode = useMatchMedia('(prefers-color-scheme: dark)');

    useEffect(() => {
        if (theme === 'System') {
            setTheme(UserProfileTheme.System);
        }
    }, [prefersDarkMode, theme, setTheme]);
};

const SyncMediaQueryPreference = () => {
    useUpdateThemeOnMediaQueryUpdate();
    return null;
};

const ThemeProvider = ({ children, initialTheme }: PropsWithChildren<{ initialTheme?: UserProfileTheme }>) => {
    const defaultTheme = initialTheme ?? getThemeCookie();
    const [theme, setTheme] = useState(defaultTheme);
    const [themeName, setThemeName] = useState<ThemeName | undefined>(
        defaultTheme === 'System' ? undefined : defaultTheme,
    );
    const handleSetTheme = useCallback((theme: UserProfileTheme) => {
        setTheme(theme);
        const colorMode = setThemeClassAndCookie(theme);

        /** if `System`, let's use the calculated `colorMode` from `setThemeClassAndCookie` to set `themeName`  */
        if (theme === 'System') {
            if (colorMode === ColorModeClass.Dark) {
                return setThemeName(UserProfileTheme.Dark);
            }
            return setThemeName(UserProfileTheme.Light);
        }
        return setThemeName(theme);
    }, []);

    return (
        <ThemeContextProvider value={{ theme, setTheme: handleSetTheme, themeName }}>
            <SyncMediaQueryPreference />
            {children}
        </ThemeContextProvider>
    );
};

export type { ThemeContextValue, ThemeName };
export { ThemeProvider, useTheme };
